import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdChecklist,
  MdCode,
  MdOndemandVideo,
  MdOutlineGamepad,
  MdOutlinePlayArrow,
  MdOutlineSubtitles,
} from "react-icons/md";
import { useDispatch } from "react-redux";
import Select from "react-select";

import empty_page from "../../../assets/selection_placeholder.svg";
import Button from "../../../components/Button";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ChallengeSelectLevelModal from "../../../components/modals/common/ChallengeSelectLevelModal";
import CodeSolutionsModal from "../../../components/modals/common/CodeSolutionsModal";
import LevelsChallengeModal from "../../../components/modals/common/LevelsChallengeModal";
import QuestionsSolutionsModal from "../../../components/modals/common/QuestionsSolutionsModal";
import WatchVideoModal from "../../../components/modals/common/WatchVideoModal";
import ExtraResouresModal from "../../../components/modals/teacher/ExtraResouresModal";
import TeacherGamesModal from "../../../components/modals/teacher/TeacherGamesModal";
import TeachersGuideModal from "../../../components/modals/teacher/TeachersGuideModal";
import { useTheme } from "../../../context/ThemeContext";
import { resetBreadcrumbs, setBreadcrumbDynamicPathNames } from "../../../redux/slices/breadcrumbsSlice";
import courseServices from "../../../services/courseServices";
import teacherServices from "../../../services/teacherServices";
import { selectStylesV1 } from "../../../styles/selectStyles";
import { getLanguage } from "../../../utils/getLanguage";

const TeacherMaterialsResources = () => {
  const { t } = useTranslation();
  const lang = getLanguage();
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const watchVideoModalRef = useRef();
  const extraResouresModalRef = useRef();
  const teachersGuideModalRef = useRef();
  const levelsChallengeModalRef = useRef();
  const teacherGamesModalRef = useRef();

  const questionsSolutionsModalRef = useRef();
  const codeSolutionsModalRef = useRef();
  const challengeSelectLevelModalRef = useRef();

  const [lessonsCount, setLessonsCount] = useState(0);
  const [challengesCount, setChallengesCount] = useState(0);
  const [skillsCount, setSkillsCount] = useState(0);
  const [teacherCourses, setTeacherCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courseDetails, setCourseDetails] = useState(null);
  const [lessons, setLessons] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null); // Added state for selected course

  const openWatchVideoModal = (lessonID, videoType) =>
    watchVideoModalRef.current?.openModal(lessonID, videoType);
  const openExtraResouresVideoModal = (courseID) => extraResouresModalRef.current?.openModal(courseID);
  const openLevelsChallengeModal = (payload) => levelsChallengeModalRef.current?.openModal(payload);

  const openGameModal = (lesson) => {
    teacherGamesModalRef.current?.openModal(lesson?.lesson?.id);
  };

  const openQuestionsSolutionsModal = (lessonID, lessonsData, index) =>
    questionsSolutionsModalRef.current?.openModal(lessonID, lessonsData, index);

  const openCodeSolutionsModal = (lessonID, codeType, lessonName, isGraduationProject) =>
    codeSolutionsModalRef.current?.openModal(lessonID, codeType, lessonName, isGraduationProject);

  const openChallengeSelectLevelModal = (lessonID, lessonName) =>
    challengeSelectLevelModalRef.current?.openModal(lessonID, lessonName);

  const handleWatchVideoSuccess = () => {
    // getTeacherClassrooms();
  };
  const handleQuestionsSolutionsModalSuccess = () => {
    // getTeacherClassrooms();
  };

  const handleChallengeSelectLevelModalSuccess = () => {
    // getTeacherClassrooms();
  };

  const handleCodeSolutionsModalSuccess = () => {
    // getTeacherClassrooms();
  };

  const getTeacherCourses = async () => {
    setLoading(true);
    try {
      const response = await teacherServices.getTeacherCourses();
      const formattedCourses = response?.map((course) => ({
        value: course?.id,
        label: course?.course,
      }));
      setTeacherCourses(formattedCourses);

      const savedCourseID = localStorage.getItem("selectedCourseId");
      if (savedCourseID) {
        const selected = formattedCourses.find((course) => course.value == savedCourseID);

        setSelectedCourse(selected || null);

        if (selected?.value) {
          handleSelect(selected);
        }
      }
    } catch (error) {
      console.error("Error fetching teacher stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const getLessons = async (courseID) => {
    setLoading(true);
    try {
      const response = await courseServices.getCourseLessons(courseID);
      const skillsCountResponse = await courseServices.getSkillsCount(courseID);
      setLessons(response);

      dispatch(setBreadcrumbDynamicPathNames([response?.classroom?.name, response?.name]));

      const lessonsArray = response?.lessons || response;

      const lessonsCount = lessonsArray.filter((item) => !item.is_challenge).length;
      const challengesCount = lessonsArray.filter((item) => item.is_challenge).length;

      setLessonsCount(lessonsCount);
      setChallengesCount(challengesCount);
      setSkillsCount(skillsCountResponse.count);
    } catch (error) {
      console.error("Error getting course details", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (selectedOption) => {
    if (selectedOption) {
      setSelectedCourse(selectedOption);
      setLoading(true);
      try {
        const response = await courseServices.getCourseDetails(selectedOption.value);
        setCourseDetails(response);
        getLessons(selectedOption.value);
        dispatch(setBreadcrumbDynamicPathNames([response?.classroom?.name, response?.name]));
      } catch (error) {
        console.error("Error getting course details", error);
      } finally {
        setLoading(false);
      }
    } else {
      setLessons([]);
      setSelectedCourse(null);
    }
  };

  useEffect(() => {
    getTeacherCourses();
    handleSelect(selectedCourse);
  }, [lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => {
      dispatch(resetBreadcrumbs());
      localStorage.removeItem("selectedCourseId");
    };
  }, []);

  return (
    <>
      <div className="mb-4 flex justify-between space-x-6">
        <div className="w-full rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface xl:w-1/3">
          <Select
            noOptionsMessage={() => <p>{t("no_options")}</p>}
            loadingMessage={() => <p>{t("loading")}</p>}
            className="z-[4]"
            isClearable={true}
            options={teacherCourses}
            value={selectedCourse}
            onChange={handleSelect}
            placeholder={t("select_course")}
            styles={selectStylesV1(theme)}
          />
        </div>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {!selectedCourse && (
            <div className="flex h-96 flex-col items-center justify-center space-y-9">
              <img height={"22%"} width={"22%"} className="opacity-75" src={empty_page} alt="" />
              <p className="text-lg font-medium text-gray-200">{t("select_level_to_view_resources")}</p>
            </div>
          )}
          {selectedCourse && (
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-start">
              {/* Left Card: Course Details */}
              <div className="flex-1 rounded-lg bg-white px-8 pb-2 pt-8 shadow-md dark:bg-dark-surface">
                <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                  {courseDetails?.course}
                </h1>

                <div className="mt-4 space-y-2 text-gray-700 dark:text-gray-300">
                  <p>
                    <span className="font-semibold">{t("lessons")}:</span> {lessonsCount}
                  </p>
                  <p>
                    <span className="font-semibold">{t("challenges")}:</span> {challengesCount}
                  </p>
                  <p>
                    <span className="font-semibold">{t("skills")}:</span> {skillsCount}
                  </p>
                </div>

                <h2 className="mt-8 text-2xl font-semibold text-gray-800 dark:text-white">
                  {t("description")}
                </h2>
                <p className="custom-scrollbar mt-4 h-[90px] overflow-scroll leading-relaxed text-gray-600 dark:text-gray-400">
                  {courseDetails?.description}
                </p>
              </div>
              {/* Right Card: Image on top, Button below */}
              <div className="w-full max-w-xs overflow-hidden rounded-lg bg-white shadow-md dark:bg-dark-surface">
                <div className="flex justify-center bg-gray-100 dark:bg-gray-700">
                  <img
                    src={courseDetails?.image}
                    alt="Smoothy Language"
                    className="h-64 w-full object-cover"
                  />
                </div>

                <div className="p-6">
                  <button
                    onClick={() => openExtraResouresVideoModal(courseDetails?.id)}
                    className="block w-full rounded-lg bg-blue-600 p-2 text-center text-lg font-bold text-white shadow-md transition-all hover:bg-blue-700"
                  >
                    {t("extra_resources")}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4">
            {(() => {
              let numberCount = 0;

              return lessons?.map((lesson, index) => {
                const isLevelsForChildren = courseDetails?.for_children;
                const title = courseDetails?.course?.toLowerCase();

                let codeType;
                if (title?.includes("web")) {
                  codeType = "web";
                } else if (title?.includes("python")) {
                  codeType = "python";
                } else {
                  codeType = "smoothy";
                }

                // Unified numbering for ordered lessons only
                let prefixLabel = "";
                if (lesson?.is_ordered) {
                  numberCount += 1;
                  prefixLabel = `${numberCount}`;
                }
                return (
                  <div
                    key={index}
                    className={`mt-3 flex items-center justify-between space-x-4 rounded-lg ${
                      lesson?.is_challenge ? "bg-white" : "bg-white"
                    } px-5 py-2 shadow-lg transition duration-300 dark:bg-dark-surface hover:dark:bg-[#29313B]`}
                  >
                    {isLevelsForChildren ? (
                      <h2 className="flex-1 border-r border-gray-200 pr-4 text-lg font-semibold text-blue-500 dark:text-white">
                        {index + 1 + ":"} {lesson?.lesson?.title}
                      </h2>
                    ) : (
                      <h2 className="flex-1 border-r border-gray-200 pr-4 text-lg font-semibold text-blue-500 dark:text-white">
                        {prefixLabel ? prefixLabel + ":" : ""} {lesson?.lesson?.title}
                      </h2>
                    )}

                    {/* Content continues here as-is */}
                    {isLevelsForChildren ? (
                      <>
                        {lesson?.is_challenge && (
                          <Button
                            color="white"
                            iconLeft={<MdOutlineSubtitles className="text-blue-500" size={26} />}
                            variant="text"
                            label={t("view_challenge")}
                            onClick={() => openLevelsChallengeModal(lesson)}
                          />
                        )}
                        {lesson?.is_project && (
                          <Button
                            color="white"
                            iconLeft={<MdOutlineSubtitles className="text-blue-500" size={26} />}
                            variant="text"
                            label={t("view_project")}
                            onClick={() => openLevelsChallengeModal(lesson)}
                          />
                        )}

                        {lesson?.is_game && (
                          <Button
                            color="white"
                            iconLeft={<MdOutlineGamepad className="text-blue-500" size={26} />}
                            variant="text"
                            label={t("view_game")}
                            onClick={() => openGameModal(lesson)}
                          />
                        )}
                        {lesson?.is_concept_lesson && (
                          <>
                            {lesson?.lesson?.has_recap && (
                              <Button
                                color="white"
                                iconLeft={<MdOutlinePlayArrow className="text-blue-500" size={26} />}
                                variant="text"
                                label={t("recap_video")}
                                onClick={() => openWatchVideoModal(lesson?.lesson?.id, "recap")}
                              />
                            )}
                            <Button
                              color="white"
                              iconLeft={<MdOndemandVideo className="text-blue-500" size={26} />}
                              variant="text"
                              label={t("watch_video")}
                              onClick={() => openWatchVideoModal(lesson?.lesson?.id, "lesson")}
                            />
                            <Button
                              color="white"
                              iconLeft={<MdChecklist className="text-blue-500" size={26} />}
                              variant="text"
                              label={t("questions_solutions")}
                              onClick={() => openQuestionsSolutionsModal(lesson?.lesson?.id, lessons, index)}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {lesson?.is_concept_lesson && (
                          <>
                            {lesson?.lesson?.has_recap && (
                              <Button
                                color="white"
                                iconLeft={<MdOutlinePlayArrow className="text-blue-500" size={26} />}
                                variant="text"
                                label={t("recap_video")}
                                onClick={() => openWatchVideoModal(lesson?.lesson?.id, "recap")}
                              />
                            )}
                            <Button
                              color="white"
                              iconLeft={<MdOndemandVideo className="text-blue-500" size={26} />}
                              variant="text"
                              label={t("watch_video")}
                              onClick={() => openWatchVideoModal(lesson?.lesson?.id, "lesson")}
                            />
                            <Button
                              color="white"
                              iconLeft={<MdChecklist className="text-blue-500" size={26} />}
                              variant="text"
                              label={t("questions_solutions")}
                              onClick={() => openQuestionsSolutionsModal(lesson?.lesson?.id, lessons, index)}
                            />
                          </>
                        )}

                        {lesson?.is_challenge && (
                          <Button
                            color="white"
                            iconLeft={<MdOutlineSubtitles className="text-blue-500" size={26} />}
                            variant="text"
                            label={t("view_all_challenges")}
                            onClick={() =>
                              openChallengeSelectLevelModal(lesson?.lesson?.id, lesson?.lesson?.title)
                            }
                          />
                        )}

                        {lesson?.is_graduation_project ? (
                          lesson?.is_code ? (
                            <Button
                              color="white"
                              iconLeft={<MdCode className="text-blue-500" size={26} />}
                              variant="text"
                              label={t("view_final_project")}
                              onClick={() =>
                                openCodeSolutionsModal(
                                  lesson?.lesson?.id,
                                  codeType,
                                  lesson?.lesson?.title,
                                  lesson?.is_graduation_project
                                )
                              }
                            />
                          ) : (
                            <Button
                              color="white"
                              iconLeft={<MdCode className="text-blue-500" size={26} />}
                              variant="text"
                              label={t("view_final_project")}
                              onClick={() =>
                                openCodeSolutionsModal(
                                  lesson?.lesson?.id,
                                  codeType,
                                  lesson?.lesson?.title,
                                  lesson?.is_graduation_project
                                )
                              }
                            />
                          )
                        ) : (
                          lesson?.is_code && (
                            <>
                              {lesson?.lesson?.has_recap && (
                                <Button
                                  color="white"
                                  iconLeft={<MdOutlinePlayArrow className="text-blue-500" size={26} />}
                                  variant="text"
                                  label={t("recap_video")}
                                  onClick={() => openWatchVideoModal(lesson?.lesson?.id, "recap")}
                                />
                              )}
                              <Button
                                color="white"
                                iconLeft={<MdOndemandVideo className="text-blue-500" size={26} />}
                                variant="text"
                                label={t("watch_video")}
                                onClick={() => openWatchVideoModal(lesson?.lesson?.id, "lesson")}
                              />
                              <Button
                                color="white"
                                iconLeft={<MdChecklist className="text-blue-500" size={26} />}
                                variant="text"
                                label={t("questions_solutions")}
                                onClick={() =>
                                  openQuestionsSolutionsModal(lesson?.lesson?.id, lessons, index)
                                }
                              />
                              <Button
                                color="white"
                                iconLeft={<MdCode className="text-blue-500" size={26} />}
                                variant="text"
                                label={t("code_solutions")}
                                onClick={() =>
                                  openCodeSolutionsModal(lesson?.lesson?.id, codeType, lesson?.lesson?.title)
                                }
                              />
                            </>
                          )
                        )}
                      </>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </>
      )}

      <ExtraResouresModal ref={extraResouresModalRef} />
      <TeachersGuideModal ref={teachersGuideModalRef} />
      <LevelsChallengeModal ref={levelsChallengeModalRef} />
      <WatchVideoModal ref={watchVideoModalRef} onSubmit={handleWatchVideoSuccess} />
      <QuestionsSolutionsModal
        ref={questionsSolutionsModalRef}
        onSubmit={handleQuestionsSolutionsModalSuccess}
      />
      <CodeSolutionsModal ref={codeSolutionsModalRef} onSubmit={handleCodeSolutionsModalSuccess} />
      <ChallengeSelectLevelModal
        ref={challengeSelectLevelModalRef}
        onSubmit={handleChallengeSelectLevelModalSuccess}
      />
      <TeacherGamesModal ref={teacherGamesModalRef} />
    </>
  );
};

export default TeacherMaterialsResources;
